import jsPDF from 'jspdf';
import type { ExportFormat } from '../types';
import type { Tables } from '@/types/database.types';

// Dashboard-inspired PDF, drawn natively in jsPDF (NOT a DOM screenshot).
// Goal: visually echoes the on-screen dashboard — full-width edge-to-edge
// brand-teal hero, KPI tiles, milestone gantt, severity-coded risks,
// priority + recent-wins lists — but rendered as crisp vector primitives
// at print quality. The Basic Format is the structural model; this is
// the more visual sibling.

type RGB = [number, number, number];
type MilestoneRow = Tables<'milestones'>;
type RiskRow = Tables<'risks'>;
type KpiRow = Tables<'kpis'>;
type TaskRow = Tables<'tasks'>;
type StatusUpdateRow = Tables<'status_updates'>;

// Ingesta brand teal palette mirrors tailwind's `ingesta-blue` scale
// (which is misnamed — actually teal). Other status colours match the
// dashboard's status-pill / severity-badge palette so the export reads
// as a print rendition of the same UI.
const COLORS: Record<string, RGB> = {
  brand: [15, 118, 110],         // teal-700 — primary brand for hero + accents
  brandDark: [17, 94, 89],       // teal-800 — gradient-darker side
  brandLight: [204, 251, 241],   // teal-100 — section card tint
  brandLighter: [240, 253, 250], // teal-50 — alt fill
  brandText: [180, 230, 220],    // ~teal-200 — secondary text on dark hero
  white: [255, 255, 255],
  text: [17, 24, 39],
  textMuted: [107, 114, 128],
  grayLight: [243, 244, 246],
  grayBorder: [229, 231, 235],
  green: [16, 185, 129],         // emerald-500
  greenDark: [4, 120, 87],
  greenBg: [220, 252, 231],
  amber: [245, 158, 11],
  amberDark: [180, 83, 9],
  amberBg: [254, 249, 195],
  red: [239, 68, 68],
  redDark: [185, 28, 28],
  redBg: [254, 226, 226],
  orange: [234, 88, 12],
  orangeBg: [255, 237, 213],
  sky: [14, 165, 233],
  violet: [139, 92, 246],
};

const healthConfig: Record<string, { label: string; color: RGB }> = {
  green: { label: 'On Track', color: COLORS.green },
  yellow: { label: 'At Risk', color: COLORS.amber },
  red: { label: 'Off Track', color: COLORS.red },
};

// Task priority → tinted text color for the Tasks "up next" list.
const taskPriorityColor: Record<string, RGB> = {
  low: COLORS.textMuted,
  medium: COLORS.amberDark,
  high: [194, 65, 12],
  urgent: COLORS.redDark,
};

// Status → dot color for tasks (mirrors the dashboard palette).
const taskStatusDot: Record<string, RGB> = {
  todo: [156, 163, 175],
  in_progress: COLORS.sky,
  complete: COLORS.green,
};

const severityConfig: Record<string, { color: RGB; bg: RGB; label: string }> = {
  low: { color: COLORS.textMuted, bg: COLORS.grayLight, label: 'LOW' },
  medium: { color: COLORS.amberDark, bg: COLORS.amberBg, label: 'MED' },
  high: { color: [194, 65, 12], bg: COLORS.orangeBg, label: 'HIGH' },
  critical: { color: COLORS.redDark, bg: COLORS.redBg, label: 'CRIT' },
};

// Cycle through this palette for KPI tiles when the KPI doesn't have a
// health_status set. Mirrors the four-colour rhythm on the dashboard.
const KPI_PALETTES: Array<{ bg: RGB; border: RGB; label: RGB; value: RGB; bar: RGB; spark: RGB }> = [
  { bg: [236, 253, 245], border: [209, 250, 229], label: COLORS.greenDark, value: [6, 78, 59], bar: COLORS.green, spark: COLORS.green },
  { bg: [240, 249, 255], border: [224, 242, 254], label: [3, 105, 161], value: [12, 74, 110], bar: COLORS.sky, spark: COLORS.sky },
  { bg: [245, 243, 255], border: [233, 213, 255], label: [109, 40, 217], value: [76, 29, 149], bar: COLORS.violet, spark: COLORS.violet },
  { bg: [255, 251, 235], border: [254, 243, 199], label: COLORS.amberDark, value: [120, 53, 15], bar: COLORS.amber, spark: COLORS.amber },
];

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr.length === 10 ? dateStr + 'T00:00:00' : dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysRemaining(endDate: string | null): number | null {
  if (!endDate) return null;
  const end = new Date(endDate.length === 10 ? endDate + 'T00:00:00' : endDate);
  if (isNaN(end.getTime())) return null;
  return Math.max(0, Math.ceil((end.getTime() - Date.now()) / 86_400_000));
}

function capitalize(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function paletteForKpi(health: string | null | undefined, index: number) {
  // yellow/red are explicit "this KPI is in trouble" signals; everything
  // else (green/null) cycles through the demo palettes by index so the
  // row reads as varied rather than monotone. Mirrors the dashboard.
  if (health === 'yellow' || health === 'red') return KPI_PALETTES[3];
  return KPI_PALETTES[index % KPI_PALETTES.length];
}

export const dashboardVisualPdfFormat: ExportFormat = {
  id: 'dashboard-visual-pdf',
  label: 'Dashboard Visual Format',
  fileFormat: 'PDF',
  description: 'Brand-styled dashboard layout — hero, KPIs, milestones, risks.',
  // ctx is unused — this format renders natively from data, not from the
  // DOM. Keeping the context arg in the signature for API consistency.
  run: async (data) => {
    // Portrait letter: matches what users print, leaves enough vertical
    // room for KPIs + a multi-row milestone gantt + risks/priorities/
    // recent-wins to live on one page for typical projects.
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;
    const contentWidth = pageWidth - margin * 2;
    let y = 0;

    const checkPageBreak = (needed: number) => {
      if (y + needed > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    // ============ HERO BANNER (edge-to-edge, dynamic height) ============
    // Full-width, no rounded corners. The height is measured from the
    // content (title + description + meta) so nothing overlaps when the
    // title wraps or the description runs long — the earlier fixed 50mm
    // banner collided the meta line with a 3-line description.
    const heroPad = margin;
    const ringR = 18;
    const ringCx = pageWidth - heroPad - ringR - 2;

    // --- Measure content first (so we can size the banner) ---
    const pillsBaselineY = 11;
    // Extra breathing room between the tracker/status pills and the title.
    const titleStartY = pillsBaselineY + 13;
    // Title gets all the width up to the completion ring (minus a gap), so
    // it stays on one line far more often than the old 0.6× clamp.
    const titleMaxW = ringCx - ringR - heroPad - 8;
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    const titleLines = doc.splitTextToSize(data.name, titleMaxW);
    const titleBlockH = titleLines.length * 8;

    let descLines: string[] = [];
    if (data.description) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      descLines = doc.splitTextToSize(data.description, pageWidth * 0.55 - heroPad).slice(0, 3);
    }
    const descStartY = titleStartY + titleBlockH + (descLines.length ? 2 : 0);
    const descBlockH = descLines.length * 4;

    // Em-dash, not the right-arrow Unicode → which isn't in the helvetica
    // glyph subset jsPDF ships with.
    const metaParts: string[] = [];
    if (data.startDate || data.endDate) {
      metaParts.push(`${formatDate(data.startDate)} — ${formatDate(data.endDate)}`);
    }
    const remaining = daysRemaining(data.endDate);
    if (remaining !== null) {
      metaParts.push(remaining === 0 ? 'Due today' : remaining === 1 ? '1 day remaining' : `${remaining} days remaining`);
    }
    const metaY = descStartY + descBlockH + (metaParts.length ? 5 : 0);

    // Hero tall enough for the content AND the completion ring.
    const heroH = Math.max(50, metaY + 5, 2 * ringR + 12);
    const ringCy = heroH / 2;

    // --- Draw the banner fill (single solid, darker teal) ---
    doc.setFillColor(...COLORS.brandDark);
    doc.rect(0, 0, pageWidth, heroH, 'F');

    // --- Pills: tracker + status ---
    let pillX = heroPad;
    if (data.trackerId) {
      const trackerText = data.trackerId.toUpperCase();
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      const trackerW = doc.getTextWidth(trackerText) + 6;
      doc.setFillColor(...COLORS.brand);
      doc.setDrawColor(180, 230, 220);
      doc.setLineWidth(0.3);
      doc.roundedRect(pillX, pillsBaselineY - 3.2, trackerW, 4.6, 2.3, 2.3, 'FD');
      doc.setTextColor(255, 255, 255);
      doc.text(trackerText, pillX + 3, pillsBaselineY);
      pillX += trackerW + 2;
    }
    const status = healthConfig[data.overallHealth] ?? healthConfig.green;
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    const statusW = doc.getTextWidth(status.label) + 6;
    doc.setFillColor(...status.color);
    doc.roundedRect(pillX, pillsBaselineY - 3.2, statusW, 4.6, 2.3, 2.3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text(status.label, pillX + 3, pillsBaselineY);

    // --- Title ---
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text(titleLines, heroPad, titleStartY);

    // --- Description (flows below the title) ---
    if (descLines.length > 0) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.brandText);
      doc.text(descLines, heroPad, descStartY);
    }

    // --- Meta row (flows below the description; never pinned/overlapping) ---
    if (metaParts.length > 0) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.brandText);
      doc.text(metaParts.join('   ·   '), heroPad, metaY);
    }

    // --- Completion ring (right side, vertically centered) ---
    // Track circle + a real progress arc (jsPDF has no arc primitive, so we
    // stroke short segments from 12 o'clock clockwise for `pct` of the way).
    const ringPct = Math.max(0, Math.min(100, data.completionPercentage ?? 0));
    doc.setLineWidth(3);
    doc.setDrawColor(40, 150, 140);
    doc.circle(ringCx, ringCy, ringR, 'S');
    if (ringPct > 0) {
      doc.setDrawColor(255, 255, 255);
      const segs = 90;
      const filled = Math.max(1, Math.round((segs * ringPct) / 100));
      const start = -Math.PI / 2; // 12 o'clock
      for (let i = 0; i < filled; i++) {
        const a1 = start + (2 * Math.PI * i) / segs;
        const a2 = start + (2 * Math.PI * (i + 1)) / segs;
        doc.line(
          ringCx + ringR * Math.cos(a1),
          ringCy + ringR * Math.sin(a1),
          ringCx + ringR * Math.cos(a2),
          ringCy + ringR * Math.sin(a2),
        );
      }
    }
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(`${Math.round(ringPct)}%`, ringCx, ringCy + 1, { align: 'center' });
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.brandText);
    doc.text('COMPLETE', ringCx, ringCy + 7, { align: 'center' });

    y = heroH + 8;

    // ============ EXECUTIVE SUMMARY ============
    if (data.executiveSummary) {
      // Set the render font BEFORE measuring — splitTextToSize measures with
      // the current font, and the body renders at 9pt. Measuring at the
      // leftover hero font (7pt) made lines ~30% too wide → right-edge clip.
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(data.executiveSummary, contentWidth - 8);
      const cardH = lines.length * 4 + 12;
      checkPageBreak(cardH + 4);
      doc.setFillColor(...COLORS.brandLight);
      doc.roundedRect(margin, y, contentWidth, cardH, 2, 2, 'F');
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.brand);
      doc.text('EXECUTIVE SUMMARY', margin + 4, y + 6);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.text);
      doc.text(lines, margin + 4, y + 11);
      y += cardH + 6;
    }

    // ============ KPI TILES ============
    const activeKpis = data.kpis.filter((k) => k.is_active !== false);
    if (activeKpis.length > 0) {
      checkPageBreak(40);
      drawSectionHeader(doc, 'Key Performance Indicators', margin, y, contentWidth);
      y += 8;

      const tilesPerRow = 4;
      const tileGap = 4;
      const tileW = (contentWidth - tileGap * (tilesPerRow - 1)) / tilesPerRow;
      const tileH = 30;

      activeKpis.forEach((kpi, idx) => {
        const col = idx % tilesPerRow;
        const row = Math.floor(idx / tilesPerRow);
        if (col === 0 && row > 0) {
          checkPageBreak(tileH + 4);
        }
        const tx = margin + col * (tileW + tileGap);
        const ty = y + row * (tileH + tileGap);
        drawKpiTile(doc, kpi, tx, ty, tileW, tileH, paletteForKpi(kpi.health_status, idx));
      });

      const rows = Math.ceil(activeKpis.length / tilesPerRow);
      y += rows * (tileH + tileGap) + 4;
    }

    // ============ MILESTONES (gantt) ============
    if (data.milestones.length > 0) {
      checkPageBreak(20);
      drawSectionHeader(doc, 'Milestones', margin, y, contentWidth);
      y += 8;
      y = drawMilestonesGantt(doc, data, margin, y, contentWidth, checkPageBreak);
    }

    // ============ RISKS + PRIORITIES (two columns) ============
    if (data.risks.length > 0 || data.priorities.length > 0) {
      const colGap = 6;
      const risksW = (contentWidth - colGap) * 0.65;
      const prioW = (contentWidth - colGap) * 0.35;
      const startY = y;
      let leftY = startY;
      let rightY = startY;

      if (data.risks.length > 0) {
        checkPageBreak(20);
        drawSectionHeader(doc, 'Risks & Mitigation', margin, leftY, risksW);
        leftY += 8;
        leftY = drawRisksList(doc, data.risks, margin, leftY, risksW, checkPageBreak);
      }
      if (data.priorities.length > 0) {
        drawSectionHeader(doc, 'This Week', margin + risksW + colGap, rightY, prioW);
        rightY += 8;
        rightY = drawPrioritiesList(doc, data.priorities, margin + risksW + colGap, rightY, prioW);
      }
      y = Math.max(leftY, rightY) + 4;
    }

    // ============ RECENT WINS ============
    if (data.accomplishments.length > 0) {
      checkPageBreak(16);
      drawSectionHeader(doc, 'Recent Wins', margin, y, contentWidth);
      y += 8;
      y = drawAccomplishmentsList(doc, data.accomplishments, margin, y, contentWidth, checkPageBreak);
    }

    // ============ TASKS ============
    // Mirrors the dashboard's Tasks section (summary + "up next"). Cancelled
    // tasks are omitted. Ordered after Recent Wins to match the on-screen tab.
    const taskable = data.tasks.filter((t) => t.status !== 'cancelled');
    if (taskable.length > 0) {
      checkPageBreak(30);
      drawSectionHeader(doc, 'Tasks', margin, y, contentWidth);
      y += 8;
      y = drawTasks(doc, taskable, margin, y, contentWidth, checkPageBreak);
    }

    // ============ FOOTER ============
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.textMuted);
      doc.text(`${data.name} — Dashboard`, margin, pageHeight - 6);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 6, { align: 'right' });
    }

    const date = new Date().toISOString().split('T')[0];
    const slug = data.name.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'project';
    doc.save(`${slug}-dashboard-${date}.pdf`);
  },
};

// ---------- Section helpers --------------------------------------------

function drawSectionHeader(doc: jsPDF, title: string, x: number, y: number, w: number) {
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.text);
  doc.text(title, x, y + 4);
  doc.setDrawColor(...COLORS.brand);
  doc.setLineWidth(0.6);
  doc.line(x, y + 6, x + w, y + 6);
}

function drawKpiTile(
  doc: jsPDF,
  kpi: KpiRow,
  x: number,
  y: number,
  w: number,
  h: number,
  palette: { bg: RGB; border: RGB; label: RGB; value: RGB; bar: RGB; spark: RGB }
) {
  doc.setFillColor(...palette.bg);
  doc.setDrawColor(...palette.border);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, w, h, 2, 2, 'FD');

  // Label
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...palette.label);
  let label = kpi.name.toUpperCase();
  while (doc.getTextWidth(label) > w - 6 && label.length > 4) {
    label = label.substring(0, label.length - 1);
  }
  doc.text(label, x + 4, y + 5);

  // Value
  const current = kpi.current_value ?? 0;
  const target = kpi.target ?? 0;
  const unit = kpi.unit ?? '';
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...palette.value);
  doc.text(formatKpiValue(current, unit), x + 4, y + 14);

  // "of {target}"
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.textMuted);
  doc.text(target > 0 ? `of ${formatKpiValue(target, unit)} target` : 'no target set', x + 4, y + 18);

  // Progress bar (only when target set)
  if (target > 0) {
    const pct = Math.max(0, Math.min(100, (current / target) * 100));
    const barY = y + h - 5;
    const barW = w - 8;
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(x + 4, barY, barW, 1.5, 0.75, 0.75, 'F');
    doc.setFillColor(...palette.bar);
    doc.roundedRect(x + 4, barY, Math.max(2, barW * (pct / 100)), 1.5, 0.75, 0.75, 'F');
  }
}

function formatKpiValue(n: number, unit: string): string {
  if (unit === '%') return `${n}%`;
  if (unit === 's') return `${n.toFixed(1)}s`;
  if (n >= 1000) return n.toLocaleString();
  return n.toString();
}

function drawMilestonesGantt(
  doc: jsPDF,
  data: { startDate: string | null; endDate: string | null; milestones: MilestoneRow[] },
  x: number,
  y: number,
  w: number,
  checkPageBreak: (n: number) => void
): number {
  const milestones = data.milestones.slice(0, 8);
  // Compute timeline: project dates first, milestones as fallback.
  const candStart: number[] = [];
  const candEnd: number[] = [];
  if (data.startDate) candStart.push(new Date(data.startDate + 'T00:00:00').getTime());
  if (data.endDate) candEnd.push(new Date(data.endDate + 'T00:00:00').getTime());
  milestones.forEach((m) => {
    if (m.start_date) candStart.push(new Date(m.start_date + 'T00:00:00').getTime());
    if (m.due_date) candEnd.push(new Date(m.due_date + 'T00:00:00').getTime());
  });
  if (candStart.length === 0 || candEnd.length === 0) {
    // No timeline — fall back to a simple list
    return drawMilestonesList(doc, milestones, x, y, w, checkPageBreak);
  }
  const tStart = Math.min(...candStart);
  const tEnd = Math.max(...candEnd);
  const tTotal = Math.max(1, tEnd - tStart);

  const nameColW = w * 0.30;
  const ownerColW = 14;
  const barColW = w - nameColW - ownerColW - 4;

  // Month markers
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.textMuted);
  const cursor = new Date(tStart);
  cursor.setDate(1);
  while (cursor.getTime() <= tEnd) {
    const monthStart = Math.max(cursor.getTime(), tStart);
    const offsetPct = (monthStart - tStart) / tTotal;
    const labelX = x + nameColW + 2 + offsetPct * barColW;
    doc.text(cursor.toLocaleDateString('en-US', { month: 'short' }), labelX, y);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  y += 2;
  doc.setDrawColor(...COLORS.grayBorder);
  doc.setLineWidth(0.2);
  doc.line(x, y, x + w, y);
  y += 1;

  milestones.forEach((m) => {
    checkPageBreak(7);
    const rowH = 6;
    // Status dot
    const dotColor = m.status === 'completed' ? COLORS.green : m.status === 'in_progress' ? COLORS.sky : m.status === 'overdue' ? COLORS.red : [156, 163, 175] as RGB;
    doc.setFillColor(...dotColor);
    doc.circle(x + 1.5, y + rowH / 2, 1.1, 'F');

    // Name
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.text);
    let name = m.name;
    while (doc.getTextWidth(name) > nameColW - 6 && name.length > 4) {
      name = name.substring(0, name.length - 2) + '…';
    }
    doc.text(name, x + 5, y + rowH / 2 + 1.4);

    // Bar track
    const barX = x + nameColW + 2;
    doc.setFillColor(...COLORS.grayLight);
    doc.roundedRect(barX, y + 1.2, barColW, rowH - 2.4, 0.8, 0.8, 'F');

    // Bar fill
    const startSrc = m.start_date ?? m.due_date;
    const endSrc = m.due_date ?? m.start_date;
    if (startSrc && endSrc) {
      const ms = new Date(startSrc + 'T00:00:00').getTime();
      const me = new Date(endSrc + 'T00:00:00').getTime();
      const left = ((ms - tStart) / tTotal) * barColW;
      let right = ((me - tStart) / tTotal) * barColW;
      if (right - left < 1.5) right = left + 1.5; // single-day visibility
      const fillColor = m.status === 'completed' ? COLORS.green : m.status === 'in_progress' ? COLORS.sky : m.status === 'overdue' ? COLORS.red : [156, 163, 175] as RGB;
      doc.setFillColor(...fillColor);
      doc.roundedRect(barX + left, y + 1.2, Math.max(1.5, right - left), rowH - 2.4, 0.8, 0.8, 'F');
    } else {
      doc.setFontSize(6);
      doc.setTextColor(...COLORS.textMuted);
      doc.text('no dates', barX + 2, y + rowH / 2 + 1);
    }

    // Owner
    const owner = m.owner_name || (m.owner_id ? '—' : '');
    if (owner) {
      doc.setFontSize(6);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.textMuted);
      const initials = owner.trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
      doc.text(initials || owner.slice(0, 2).toUpperCase(), x + w - ownerColW / 2, y + rowH / 2 + 1, { align: 'center' });
    }

    y += rowH + 0.5;
  });
  return y + 2;
}

function drawMilestonesList(
  doc: jsPDF,
  milestones: MilestoneRow[],
  x: number,
  y: number,
  w: number,
  checkPageBreak: (n: number) => void
): number {
  milestones.forEach((m) => {
    checkPageBreak(6);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.text);
    doc.text(m.name, x + 2, y + 4);
    doc.setTextColor(...COLORS.textMuted);
    doc.text(m.due_date ? formatDate(m.due_date) : '—', x + w - 30, y + 4, { align: 'right' });
    doc.text(capitalize(m.status || 'not_started'), x + w - 2, y + 4, { align: 'right' });
    y += 5;
  });
  return y + 2;
}

function drawRisksList(
  doc: jsPDF,
  risks: RiskRow[],
  x: number,
  y: number,
  w: number,
  checkPageBreak: (n: number) => void
): number {
  // Sort by severity desc
  const rank: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
  const sorted = [...risks].sort((a, b) => (rank[b.severity] ?? 0) - (rank[a.severity] ?? 0)).slice(0, 6);

  sorted.forEach((r) => {
    const sev = severityConfig[r.severity] ?? severityConfig.medium;
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    const titleLines = doc.splitTextToSize(r.summary, w - 18);
    const blockH = titleLines.length * 4 + 6;
    checkPageBreak(blockH);

    // Severity left bar
    doc.setFillColor(...sev.color);
    doc.rect(x, y, 1.5, blockH, 'F');
    // Card bg
    doc.setFillColor(...COLORS.grayLight);
    doc.roundedRect(x + 2, y, w - 2, blockH, 1, 1, 'F');

    // Severity badge
    doc.setFillColor(...sev.bg);
    doc.roundedRect(x + 4, y + 1.5, 9, 3.5, 0.5, 0.5, 'F');
    doc.setFontSize(6);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...sev.color);
    doc.text(sev.label, x + 4 + 4.5, y + 4, { align: 'center' });

    // Title
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.text);
    doc.text(titleLines, x + 16, y + 4);

    // Meta
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.textMuted);
    const metaParts = [`Status: ${capitalize(r.status || 'open')}`];
    if (r.owner_name) metaParts.push(`Owner: ${r.owner_name}`);
    doc.text(metaParts.join('   ·   '), x + 16, y + titleLines.length * 4 + 4);

    y += blockH + 1.5;
  });
  return y + 1;
}

function drawPrioritiesList(
  doc: jsPDF,
  priorities: StatusUpdateRow[],
  x: number,
  y: number,
  w: number
): number {
  // Set the body font before measuring so wrap widths match the render.
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  priorities.slice(0, 6).forEach((p) => {
    const lines = doc.splitTextToSize(p.content, w - 6);
    const blockH = lines.length * 3.5 + 1;
    // Left accent bar
    doc.setFillColor(...COLORS.brand);
    doc.rect(x, y, 0.8, blockH, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.text);
    doc.text(lines, x + 3, y + 3);
    if (p.week_of) {
      doc.setFontSize(6);
      doc.setTextColor(...COLORS.textMuted);
      doc.text(`Week of ${formatDate(p.week_of)}`, x + 3, y + lines.length * 3.5 + 2.5);
    }
    y += blockH + (p.week_of ? 4 : 1.5);
  });
  return y;
}

function drawAccomplishmentsList(
  doc: jsPDF,
  accomplishments: StatusUpdateRow[],
  x: number,
  y: number,
  w: number,
  checkPageBreak: (n: number) => void
): number {
  // Set the body font before measuring so wrap widths match the render.
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  accomplishments.slice(0, 6).forEach((a) => {
    const lines = doc.splitTextToSize(a.content, w - 8);
    const blockH = lines.length * 3.8 + 1;
    checkPageBreak(blockH);
    // Green check bullet
    doc.setFillColor(...COLORS.greenBg);
    doc.circle(x + 2, y + 2, 1.6, 'F');
    doc.setDrawColor(...COLORS.greenDark);
    doc.setLineWidth(0.4);
    // Faux check (two short strokes)
    doc.line(x + 1.2, y + 2, x + 1.9, y + 2.6);
    doc.line(x + 1.9, y + 2.6, x + 2.9, y + 1.5);

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.text);
    doc.text(lines, x + 6, y + 2.5);
    if (a.created_at) {
      doc.setFontSize(6);
      doc.setTextColor(...COLORS.textMuted);
      doc.text(formatDate(a.created_at), x + 6, y + lines.length * 3.8 + 1.5);
    }
    y += blockH + (a.created_at ? 3 : 1.5);
  });
  return y;
}

// Tasks section for the visual export: a compact summary tile row plus an
// "up next" list of the next few unfinished tasks by due date. Cancelled
// tasks are already filtered out by the caller.
function drawTasks(
  doc: jsPDF,
  tasks: TaskRow[],
  x: number,
  y: number,
  w: number,
  checkPageBreak: (n: number) => void
): number {
  const todo = tasks.filter((t) => t.status === 'todo').length;
  const inProgress = tasks.filter((t) => t.status === 'in_progress').length;
  const done = tasks.filter((t) => t.status === 'complete').length;
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  // Summary tiles
  const tiles: { label: string; value: string; color: RGB; bg: RGB }[] = [
    { label: 'To Do', value: String(todo), color: COLORS.textMuted, bg: COLORS.grayLight },
    { label: 'In Progress', value: String(inProgress), color: [3, 105, 161], bg: [240, 249, 255] },
    { label: 'Complete', value: String(done), color: COLORS.greenDark, bg: COLORS.greenBg },
    { label: 'Done', value: `${pct}%`, color: COLORS.brand, bg: COLORS.brandLight },
  ];
  const gap = 4;
  const tw = (w - gap * 3) / 4;
  const th = 15;
  checkPageBreak(th + 6);
  tiles.forEach((t, i) => {
    const tx = x + i * (tw + gap);
    doc.setFillColor(...t.bg);
    doc.roundedRect(tx, y, tw, th, 2, 2, 'F');
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.textMuted);
    doc.text(t.label.toUpperCase(), tx + 3, y + 5);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...t.color);
    doc.text(t.value, tx + 3, y + 12);
  });
  y += th + 6;

  // Up next — unfinished tasks by due date (soonest first, no-date last)
  const upNext = tasks
    .filter((t) => t.status === 'todo' || t.status === 'in_progress')
    .sort((a, b) => {
      if (a.due_date && b.due_date) return a.due_date < b.due_date ? -1 : a.due_date > b.due_date ? 1 : 0;
      if (a.due_date) return -1;
      if (b.due_date) return 1;
      return 0;
    })
    .slice(0, 5);

  if (upNext.length > 0) {
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.textMuted);
    doc.text('UP NEXT', x, y);
    y += 4;

    upNext.forEach((t) => {
      checkPageBreak(6);
      // Status dot
      doc.setFillColor(...(taskStatusDot[t.status] ?? COLORS.textMuted));
      doc.circle(x + 1.2, y + 1.6, 1, 'F');

      // Right side: priority (tinted) + due date (muted), right-aligned
      const priTxt = capitalize(t.priority);
      const dueTxt = t.due_date ? formatDate(t.due_date) : '';
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      const dueW = dueTxt ? doc.getTextWidth(dueTxt) : 0;
      if (dueTxt) {
        doc.setTextColor(...COLORS.textMuted);
        doc.text(dueTxt, x + w, y + 2.5, { align: 'right' });
      }
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...(taskPriorityColor[t.priority] ?? COLORS.textMuted));
      doc.text(priTxt, x + w - dueW - (dueTxt ? 4 : 0), y + 2.5, { align: 'right' });
      const rightReserve = dueW + (dueTxt ? 4 : 0) + doc.getTextWidth(priTxt) + 4;

      // Title (truncated to leave room for the right-side meta)
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.text);
      let title = t.title;
      const maxTitleW = w - 5 - rightReserve;
      while (doc.getTextWidth(title) > maxTitleW && title.length > 4) {
        title = title.substring(0, title.length - 2) + '…';
      }
      doc.text(title, x + 5, y + 2.5);

      y += 5.5;
    });
  }
  return y + 2;
}
